import {create} from "zustand"
import {produce} from "immer"
import {User, UserInfo} from "@/types/User";
import {Registration} from "@/types/Registration";
import {PlaceCategory, PlaceType} from "@/types/Places";
import {endpoint} from "@/utils/api/Conf";
import {Scan} from "@/types/Scan";
import {RequestError} from "@/types/RequestError";
import {WalkActionResult, WalkStatus} from "@/types/Walk";
import {Path} from "@/types/Paths";
import {Notification} from "@/types/Notification";
import {Segment} from "@/types/Segments";
import {SearchFilters} from "@/types/Search";
import {Cartula} from "@/types/Cartula";
import {AppMessage} from "@/types/AppMessages";

interface AppState {
  splash: boolean,
  loginPopup: boolean,
  menu: boolean,
  user?: User
  userInfo?: UserInfo
  refreshToken: boolean
  fetching: boolean
  errors: Array<RequestError | undefined> | undefined
  messages?: Array<AppMessage> | undefined
  showAbandon: boolean
  results: WalkActionResult | undefined
  walkStatus: WalkStatus | undefined
  scanning: boolean
  scanningAction: string | undefined
  scanningTransport?: 'foot' | 'bicycle'
  scanningDirection?: 'normal' | 'inverted'
  scanningPath?: Path
  scanningSegments?: Array<Segment>
  scannedQrCode?: false | Array<Scan>
  loggedInChecking: boolean
  registration?: Registration
  cartula?: Cartula
  types?: Array<PlaceType>
  cats?: Array<PlaceCategory>
  notifications: Array<Notification>
  searchFilters: SearchFilters
  reloadSearch: boolean
  mapBounds?: google.maps.LatLngBounds
  currentGpxPoint?: google.maps.LatLng
}

type AppActions = {
  setSplash: (value: boolean) => void
  toggleMenu: () => void
  setRefresh: (value: boolean) => void
  setError: (value: RequestError | undefined) => void
  setMessage: (value: AppMessage) => void
  setMessages: (value: Array<AppMessage> | undefined) => void
  setShowAbandon: (value: boolean) => void
  setErrors: (value: Array<RequestError | undefined> | undefined) => void
  setReloadSearch: (value: boolean) => void
  setLoginPopup: (value: boolean) => void
  setScanning: (value: boolean) => void
  setScanningAction: (value: string | undefined) => void
  setScanningTransport: (value: 'foot' | 'bicycle' | undefined) => void
  setScanningDirection: (value: 'normal' | 'inverted' | undefined) => void
  setScanningPath: (value: Path | undefined) => void
  setScanningSegments: (value: Array<Segment> | undefined) => void
  setLoggedInChecking: (value: boolean) => void
  segmentAction: (authToken: string, action: Scan, locale: string) => void
  loadWalkStatus: (authToken: string) => Promise<WalkStatus | false>
  loadUserInfo: (authToken: string) => Promise<UserInfo | false>
  setUser: (value: User | undefined) => void
  setUserInfo: (value: UserInfo | undefined) => void
  setRegistration: (value: Registration | undefined) => void
  setCurrentGpxPoint: (value: google.maps.LatLng | undefined) => void
  setCartula: (value: Cartula | undefined) => void
  setTypes: (value: Array<PlaceType> | undefined) => void
  setCats: (value: Array<PlaceCategory> | undefined) => void
  setQrCode: (value: false | Array<Scan> | undefined) => void
  addNotification: (value: Notification) => void
  removeNotification: (index: number) => void
  setMapBounds: (value: google.maps.LatLngBounds | undefined) => void
  cleanResultsAndErrors: () => void
  setSearchFilter: (value: string, field: "search" | "type" | "category" | "segment" | "page" | "page_size" | "latitude" | "longitude" | "radius") => void
}

export const useAppStore = create<AppState & AppActions>()((set, get) => ({
  splash: true,
  loginPopup: false,
  menu: false,
  showAbandon: false,
  scanning: false,
  fetching: false,
  errors: undefined,
  scanningAction: undefined,
  walkStatus: undefined,
  results: undefined,
  loggedInChecking: true,
  notifications: [],
  refreshToken: false,
  searchFilters: {
    search: undefined,
    category: undefined,
    type: undefined,
    segment: undefined,
    page: undefined,
    page_size: '12',
  },
  reloadSearch: false,
  setMessage: async (value: AppMessage) => {
    const msgs = get().messages || []
    msgs.push(value)
    set({messages: msgs})
  },
  setMessages: async (value: Array<AppMessage> | undefined) => {
    set({messages: value})
  },
  setError: async (value: RequestError | undefined) => {
    const errs = get().errors || []
    errs.push(value)
    set({errors: errs})
  },
  setErrors: async (value: Array<RequestError | undefined> | undefined) => {
    set({errors: value})
  },
  setShowAbandon: async (value: boolean) => {
    set({showAbandon: value})
  },
  setMapBounds: async (bounds?: google.maps.LatLngBounds) => {
    set({mapBounds: bounds})
  },
  setCurrentGpxPoint: async (point?: google.maps.LatLng) => {
    set({currentGpxPoint: point})
  },
  segmentAction: async (authToken: string, action: Scan, locale: string = 'en') => {
    set(() => ({fetching: true}))
    const init = {
      method: "POST",
      contentType: "application/json",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        'Authorization': `Bearer ${authToken}`,
        'X-Language': locale,
      },
      body: JSON.stringify({actions: [action]})
    }
    try {
      const request = await fetch(endpoint + `walk/action`, init)
      const body = await request.json() as WalkActionResult | RequestError
      if ('error_code' in body) {
        if (body.error_code === "DIFFERENT_PATH") {
          set(() => ({fetching: false, showAbandon: true}))
        }
        set(() => ({error: body, fetching: false}))
        get().setError(body)
      } else {
        set(() => ({results: body, fetching: false}))
      }
    } catch (err) {
      console.log('ERROR', err)
      set({fetching: false})
      get().setError({message: 'ERRORE', error_code: '500'})
    }
  },
  loadWalkStatus: async (authToken: string): Promise<WalkStatus | false> => {
    const init = {
      method: "GET",
      contentType: "application/json",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        'Authorization': `Bearer ${authToken}`,
      },
    }
    try {
      const request = await fetch(endpoint + `walk/status`, init)
      if (!request.ok) {
        set(() => ({
          refreshToken: true
        }));
        return false
      }
      const body = await request.json() as WalkStatus | RequestError
      if ('error_code' in body) {
        set(() => ({fetching: false}))
        get().setErrors([body])
        return false
      } else {
        set(() => ({fetching: false}))
        return body
      }
    } catch (err) {
      if (err instanceof TypeError) {
        console.log('TypeError', err)
        set(() => ({
          fetching: false,
          refreshToken: true
        }));
        get().setError({message: 'Errore CORS o di rete', error_code: 'CORS'})
      }
      console.log('ERROR', err)
      set({fetching: false})
      get().setError({message: 'ERRORE', error_code: '500'})

      return false
    }
  },
  loadUserInfo: async (authToken: string): Promise<UserInfo | false> => {
    set(() => ({fetching: true}))
    const init = {
      method: "GET",
      contentType: "application/json",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        'Authorization': `Bearer ${authToken}`,
      },
    }
    try {
      const request = await fetch(endpoint + `account/info`, init)
      const body = await request.json() as UserInfo | RequestError
      if ('error_code' in body) {
        set(() => ({fetching: false}))
        get().setError(body)
        return false
      } else {
        set(() => ({fetching: false, userInfo: body}))
        return body
      }
    } catch (err) {
      console.log('ERROR', err)
      set({fetching: false})
      get().setError({message: 'ERRORE', error_code: '500'})
      return false
    }
  },
  setSearchFilter: (value: string, field: "search" | "type" | "category" | "segment" | "page" | "page_size" | "latitude" | "longitude" | "radius") =>
    set(
      produce((state: AppState) => {
        const current: SearchFilters = state.searchFilters
        current[field] = value
        state.searchFilters = current
      })
    ),
  setSplash: (value: boolean) =>
    set(
      produce((state: AppState) => {
        state.splash = value
      })
    ),
  setReloadSearch: (value: boolean) =>
    set(
      produce((state: AppState) => {
        state.reloadSearch = value
      })
    ),
  setLoginPopup: (value: boolean) =>
    set(
      produce((state: AppState) => {
        state.loginPopup = value
      })
    ),
  toggleMenu: () =>
    set(
      produce((state: AppState) => {
        state.menu = !state.menu
      })
    ),
  addNotification: (value: Notification) =>
    set(
      produce((state: AppState) => {
        state.notifications = [
          ...state.notifications,
          value
        ]
      })
    ),
  setUserInfo: (value: UserInfo | undefined) =>
    set(
      produce((state: AppState) => {
        state.userInfo = value
      })
    ),
  removeNotification: (index: number) =>
    set(
      produce((state: AppState) => {
        state.notifications = state.notifications.slice(0, index).concat(state.notifications.slice(index + 1));
      })
    ),
  setQrCode: (value: false | Array<Scan> | undefined) =>
    set(
      produce((state: AppState) => {
        state.scannedQrCode = value
      })
    ),
  setRefresh: (value: boolean) =>
    set(
      produce((state: AppState) => {
        state.refreshToken = value
      })
    ),
  setScanning: (value: boolean) =>
    set(
      produce((state: AppState) => {
        state.scanning = value
      })
    ),
  setScanningAction: (value: string | undefined) =>
    set(
      produce((state: AppState) => {
        state.scanningAction = value
      })
    ),
  setScanningTransport: (value: 'foot' | 'bicycle' | undefined) =>
    set(
      produce((state: AppState) => {
        state.scanningTransport = value
      })
    ),
  setScanningPath: (value: Path | undefined) =>
    set(
      produce((state: AppState) => {
        state.scanningPath = value
      })
    ),
  setScanningSegments: (value: Array<Segment> | undefined) =>
    set(
      produce((state: AppState) => {
        state.scanningSegments = value
      })
    ),
  setScanningDirection: (value: 'normal' | 'inverted' | undefined) =>
    set(
      produce((state: AppState) => {
        state.scanningDirection = value
      })
    ),
  setLoggedInChecking: (value: boolean) =>
    set(
      produce((state: AppState) => {
        state.loggedInChecking = value
      })
    ),
  setUser: (value: User | undefined) =>
    set(
      produce((state: AppState) => {
        state.user = value
      })
    ),
  setRegistration: (value: Registration | undefined) =>
    set(
      produce((state: AppState) => {
        state.registration = value
      })
    ),
  setCartula: (value: Cartula | undefined) =>
    set(
      produce((state: AppState) => {
        state.cartula = value
      })
    ),
  setTypes: (value: Array<PlaceType> | undefined) =>
    set(
      produce((state: AppState) => {
        state.types = value
      })
    ),
  setCats: (value: Array<PlaceCategory> | undefined) =>
    set(
      produce((state: AppState) => {
        state.cats = value
      })
    ),
  cleanResultsAndErrors: () =>
    set(
      produce((state: AppState) => {
        state.results = undefined
        state.errors = undefined
      })
    ),
}))
